/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';

import type { ModalProps } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import Modal from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import type { FixedSessionFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedEnterpriseEntitiesFragments';

import _t from 'i18n!nls/xdp';

const styles = {
  availabilities: () => css`
    margin: 0;
    border: 1px solid var(--cds-color-grey-200);
    border-radius: 4px;
    display: inline-flex;
    padding: var(--cds-spacing-200) 0;
    width: 100%;

    li {
      padding: 0 var(--cds-spacing-300);
      width: 50%;
    }

    li + li {
      border-left: 1px solid var(--cds-color-grey-200);
    }
  `,
  label: css`
    font-weight: 600;
  `,
  spacer: () => css`
    margin-top: var(--cds-spacing-400);
  `,
};

type PropsFromCaller = ModalProps & {
  productName: string;
  isSelectedForCredit?: boolean;
  childrenHaveFixedSessions?: boolean;
  fixedSession?: FixedSessionFragment | null;
};

const AvailabilityDetailsModal = ({
  productName,
  childrenHaveFixedSessions,
  isSelectedForCredit,
  fixedSession,
  ...rest
}: PropsFromCaller) => {
  return (
    <Modal
      {...rest}
      header={_t('Availability details')}
      supportText={
        fixedSession
          ? _t("This #{productName} must be completed by its end date. You can't reset deadlines.", { productName })
          : undefined
      }
    >
      {childrenHaveFixedSessions ? (
        <Typography2 component="p">
          {_t(
            // eslint-disable-next-line no-restricted-syntax
            'Some of the courses in this #{productName} are offered with fixed start and end dates. Please check the course page for more details.',
            { productName }
          )}
        </Typography2>
      ) : null}
      {fixedSession ? (
        <React.Fragment>
          <ul css={styles.availabilities}>
            <li>
              <Typography2 variant="bodySecondary" color="supportText" component="div" css={styles.label}>
                {_t('Available from')}
              </Typography2>
              <Typography2 variant="bodyPrimary" component="div">
                {formatDateTimeDisplay(fixedSession.startsAt, LONG_DATE_ONLY_DISPLAY)}
              </Typography2>
            </li>
            <li>
              <Typography2 variant="bodySecondary" color="supportText" component="div" css={styles.label}>
                {_t('Available until')}
              </Typography2>
              <Typography2 variant="bodyPrimary" component="div">
                {formatDateTimeDisplay(fixedSession.endsAt, LONG_DATE_ONLY_DISPLAY)}
              </Typography2>
            </li>
          </ul>
        </React.Fragment>
      ) : null}
      {(childrenHaveFixedSessions || fixedSession) && isSelectedForCredit && <div css={styles.spacer} />}
      {isSelectedForCredit ? (
        <React.Fragment>
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('Earn credit towards your degree')}
          </Typography2>
          <Typography2 component="p">
            {_t('Your institution will award credit towards your degree for completing this #{productName}.', {
              productName,
            })}
          </Typography2>
        </React.Fragment>
      ) : null}
    </Modal>
  );
};

export default AvailabilityDetailsModal;
